import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { ApplicationsService } from '../../../orval-gen/endpoints/applications/applications.service';
import { AgencyApplicationsList200DataItemConnectionStatus } from '../../../orval-gen/models';
import { skipErrorToast } from '../../interceptors/error.interceptor';
import { FilterPermissionsPipe } from '../../pipes/filter-permissions.pipe';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { unwrap } from '../../utils/http-utils';
import {
  Application,
  appInitials,
  canDrop,
  toggleConfirmation,
  toggleConnection,
  toggleLabel,
  toggleSuccessMessage,
} from './application-utils';

/** One tab per connection status, as on the Developer Portal, plus the apps not yet requested. */
type ApplicationsTab = AgencyApplicationsList200DataItemConnectionStatus | 'available';

// What an agency uses and can request first, then the requests that are not (or no longer) in use.
const TABS: { key: ApplicationsTab; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'available', label: 'Available' },
  { key: 'pending', label: 'Pending' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'rejected', label: 'Rejected' },
];

const DEFAULT_TAB: ApplicationsTab = 'active';

@Component({
  selector: 'app-applications',
  imports: [FilterPermissionsPipe, FormsModule, RouterLink, IconComponent, ConfirmDialogComponent],
  templateUrl: './applications.component.html',
  styleUrl: './applications.component.scss',
})
export class ApplicationsComponent implements OnInit {
  loaded: boolean = false;
  applications: Application[] = [];
  readonly tabs = TABS;
  activeTab: ApplicationsTab = DEFAULT_TAB;
  selectedApp: Application | null = null;
  modalVisible = false;
  saving = false;
  /** App ID typed to request a private application, which the listing does not offer. */
  requestId = '';
  requesting = false;
  requestError = '';
  readonly initials = appInitials;
  readonly toggleLabel = toggleLabel;
  readonly canDrop = canDrop;
  /** The connections are the consumer's, so only managers of its super agency may see or change them. */
  readonly canManage: boolean;

  constructor(
    private applicationsService: ApplicationsService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    ls: LocalStorageService
  ) {
    this.canManage = ls.getBool('isConsumerOwner');
  }

  ngOnInit() {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    this.activeTab = TABS.find(t => t.key === tab)?.key ?? DEFAULT_TAB;
    this.updateTabInUrl(this.activeTab);

    if (this.canManage) {
      this.getApplications();
    } else {
      this.loaded = true;
    }
  }

  getApplications() {
    this.loaded = false;

    this.applicationsService
      .agencyApplicationsList()
      .pipe(
        unwrap(),
        finalize(() => (this.loaded = true))
      )
      .subscribe({
        next: data => {
          this.applications = data ?? [];
        },
        error: error => {
          console.error('Failed to load applications:', error);
        },
      });
  }

  /** The apps on a tab: those whose connection has that status, or those with none on Available. */
  appsFor(tab: ApplicationsTab): Application[] {
    return this.applications.filter(app => (app.connection?.status ?? 'available') === tab);
  }

  setTab(tab: ApplicationsTab): void {
    this.activeTab = tab;
    this.updateTabInUrl(tab);
  }

  updateTabInUrl(tab: ApplicationsTab): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: tab === DEFAULT_TAB ? { tab: null } : { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  confirmToggle() {
    const app = this.selectedApp;
    if (!app) return;

    this.saving = true;
    toggleConnection(this.applicationsService, app)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          // A request starts pending, so follow it there; a dropped connection leaves the tab it was on.
          if (!app.connection) this.setTab('pending');
          this.getApplications();
          this.toastr.success(toggleSuccessMessage(app), 'Success');
          this.closeModal();
        },
        error: err => {
          console.error('Failed to update application status:', err);
          this.closeModal();
        },
      });
  }

  /** Requests a private application by the ID its developer shared; the connection starts pending like any other. */
  requestPrivateApp(): void {
    const agwId = this.requestId.trim();
    if (!agwId) return;

    this.requestError = '';
    this.requesting = true;
    this.applicationsService
      .agencyApplicationsRequest({ agw_id: agwId }, { context: skipErrorToast() })
      .pipe(finalize(() => (this.requesting = false)))
      .subscribe({
        next: () => {
          this.requestId = '';
          this.toastr.success(
            `Access to ${agwId} requested. The app can use your content once its developer approves.`,
            'Success'
          );
          this.setTab('pending');
          this.getApplications();
        },
        error: err => {
          // 404 covers an unknown ID as well as an inactive app or one that cannot be requested.
          if (err?.status === 404) {
            this.requestError = 'No application with this ID can be requested.';
          } else if (err?.status === 409) {
            this.requestError = 'This application has already been requested.';
          } else {
            this.requestError = err?.error?.detail ?? 'Failed to request the application.';
          }
          console.error('Failed to request application:', err);
        },
      });
  }

  openConfirmationModal(app: Application): void {
    this.selectedApp = app;
    this.modalVisible = true;
  }

  closeModal(): void {
    this.modalVisible = false;
    this.selectedApp = null;
  }

  get confirmation(): { title: string; message: string } {
    return this.selectedApp ? toggleConfirmation(this.selectedApp) : { title: '', message: '' };
  }
}
